import { FeatureFlag } from 'config';
import { capitalize } from 'utils/strings';
import { isCreditCardExpired } from 'utils/dateTimes';
import type { ISubscription } from './Subscription';
import { SubscriptionStatus } from './Subscription';
import { IBasicAddress } from './Address';
import BillingAddress from './BillingAddress';

type PaymentMethod = {
  provider_name: string;
  cc_last_four: string;
  cc_brand: string;
  cc_exp_month: string;
  cc_exp_year: string;
  customer_email: string;
}

interface ICurrentUser {
  id: number;
  email: string;
  generated_password: boolean;
  display_name: string;
  unsent_givaways_count: number;
  subscription: ISubscription;
  credit: number;
  payment_method: PaymentMethod;
  registered_at: Date;
  admin: boolean;
  employee: boolean;
  favorite_count: number;
  last_delivered_order_id?: number;
  billing_address: IBasicAddress;
  coupon_message: string;
  coupon_tooltip_message: string;
  active_coupon: boolean;

  readonly hasGiveaways?: boolean;
  readonly giveawayCount?: number;
  readonly hasValidPaymentMethod?: boolean;
  readonly subscriptionStatus?: string;
  readonly isDeactivated?: boolean;
  readonly isSuspended?: boolean;
  readonly isCancelled?: boolean;
  readonly isReturning?: boolean;
  readonly isInactive?: boolean;
  readonly hasFavoritesEnabled?: boolean;
  readonly isEmpty?: boolean;
  readonly canEditPlan?: boolean;
  readonly isAdmin?: boolean;
  readonly isEmployee?: boolean;
  readonly isGiftUser?: boolean
  readonly feature_flags: Record<FeatureFlag, boolean>;
  readonly user_feature_flags: Record<string, boolean>;
}

class CurrentUser implements ICurrentUser {
  id: number;
  email: string;
  generated_password: boolean;
  display_name: string;
  unsent_givaways_count: number;
  subscription: ISubscription;
  credit: number;
  payment_method: PaymentMethod;
  registered_at: Date;
  admin: boolean;
  employee: boolean;
  favorite_count: number;
  last_delivered_order_id?: number;
  billing_address: BillingAddress;
  coupon_message: string;
  coupon_tooltip_message: string;
  active_coupon: boolean;

  readonly feature_flags: Record<string, boolean>;
  readonly user_feature_flags: Record<string, boolean>;

  constructor(attributes?: ICurrentUser) {
    Object.assign(this, attributes || {});
    // @ts-expect-error TODO: fix null safety
    this.billing_address = new BillingAddress(attributes?.billing_address || '');
  }

  hasFeatureEnabled(feature: FeatureFlag): boolean {
    return !!(this.feature_flags && this.feature_flags[feature]);
  }

  hasUserFeatureEnabled(feature: string): boolean {
    return !!(this.user_feature_flags && this.user_feature_flags[feature]);
  }

  static isDeactivated(status: SubscriptionStatus): boolean {
    return [
      SubscriptionStatus.CANCELLED,
      SubscriptionStatus.PAUSED,
      SubscriptionStatus.SUSPENDED,
      SubscriptionStatus.RETURNING,
      SubscriptionStatus.INACTIVE,
    ].includes(status);
  }

  get hasGiveaways(): boolean {
    return !this.isDeactivated && !!this.unsent_givaways_count;
  }

  get giveawayCount(): number {
    return this.unsent_givaways_count;
  }

  get hasValidPaymentMethod(): boolean {
    if (!this.payment_method) return !!this.credit;

    const { cc_exp_month, cc_exp_year } = this.payment_method;

    return this.payment_method.cc_brand
      ? !isCreditCardExpired(cc_exp_month, cc_exp_year)
      : true;
  }

  get subscriptionStatus(): string {
    if (!this.subscription) return '';

    const nameMappings = {
      [SubscriptionStatus.RETURNING]: 'Paused',
      [SubscriptionStatus.SKIPPED]: 'Active',
      [SubscriptionStatus.FORCED_SKIP]: 'Active',
    };

    return nameMappings[this.subscription.status]
      || capitalize(this.subscription.status);
  }

  get isDeactivated(): boolean {
    return CurrentUser.isDeactivated(this.subscription?.status);
  }

  get isSuspended(): boolean {
    return this.subscription?.status === SubscriptionStatus.SUSPENDED;
  }

  get isCancelled(): boolean {
    return this.subscription?.status === SubscriptionStatus.CANCELLED;
  }

  /**
   * The 'paused' and 'returning' subscription states are conceptually the
   * same thing, but 'paused' is deprecated and not used anymore. We check
   * for it for safety reasons, cause some old users could still have it.
   */
  get isReturning(): boolean {
    return this.subscription?.status === SubscriptionStatus.RETURNING
      || this.subscription?.status === SubscriptionStatus.PAUSED;
  }

  get isInactive(): boolean {
    return this.subscription?.status === SubscriptionStatus.INACTIVE;
  }

  get hasFavoritesEnabled(): boolean {
    return ![
      SubscriptionStatus.CANCELLED,
      SubscriptionStatus.SUSPENDED,
    ].includes(this.subscription?.status);
  }

  get isEmpty(): boolean {
    return !this.email;
  }

  get canEditPlan(): boolean {
    return (this.payment_method
      && ![
        SubscriptionStatus.CANCELLED,
        SubscriptionStatus.RETURNING,
        SubscriptionStatus.PAUSED,
      ].includes(this.subscription?.status)
    );
  }

  get isAdmin(): boolean {
    return this.admin;
  }

  get isEmployee(): boolean {
    return this.employee;
  }
}

export { ICurrentUser, PaymentMethod, SubscriptionStatus };
export default CurrentUser;
